import dayjs from 'dayjs'
import { useReducer, useState } from 'react'

import { SchoolService } from '@services/SchoolService'
import { SubscriptionService } from '@services/SubscriptionService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { SubscriptionStatus, TermType, Product, Subscription } from '../../../../../types'
import { PromiseThrottle } from '../../utils/PromiseThrottle'

import { getYearLabel } from './getYear'
import { normalize } from './normalize'

type RowStatusTypes = 'DRAFT' | 'INVALID' | 'COMPLETE' | 'FAILED' | 'READY_TO_PROCESS' | 'VERIFICATION_FAILED'
export const STEPS = {
    NOT_STARTED: 0,
    READY_TO_VERIFY: 1,
    READY_TO_PROCESS: 1,
    PROCESSING: 2,
    COMPLETE: 3,
    CAN_NOT_PROCESS: 0
}

type ActionTypes = 'NewSchool' | 'ExistingSchool'

export type MessageType = 'ERROR' | 'NOTICE' | 'WARN'

export type ProcessingStatus = keyof typeof STEPS

type SubscriptionValues = {
    schoolName: string
    adminName?: string
    seats: number
    adminEmails: string[]
    products: Product[]
    postalCode?: string
    domain: string
    govId?: string
    keyword?: string
    country?: string
}

export type BatchRow = {
    values: SubscriptionValues
    isValid: boolean
    status: RowStatusTypes
    errors: { message: string; type: MessageType }[]
    actionType: ActionTypes | null
    schoolId?: string
    seatsComparison?: SeatsComparison
}

export type SeatsComparison = {
    old: number
    new: number
}

type RowAction =
    | {
          type: 'NewSchool'
      }
    | { type: 'ExistingSchool'; schoolId: string; seatsComparison: SeatsComparison }

type SubscriptionsBySchool = { [key: string]: Subscription[] }

type Action =
    | { type: 'INIT'; payload: { rows: BatchRow[] } }
    | { type: 'UPDATE_ROW_STATUS'; payload: { index: number; status?: RowStatusTypes; isValid?: boolean } }
    | { type: 'UPDATE_ROW_ACTION'; payload: { index: number; rowAction: RowAction } }
    | { type: 'ADD_MESSAGE'; payload: { index: number; message: string; type: MessageType } }
    | { type: 'UPDATE_START_DATE'; payload: { startDate: Date } }
    | { type: 'UPDATE_COUNTRY'; payload: { country: string } }
    | { type: 'UPDATE_EXISTING_SUBSCRIPTIONS'; payload: { subscriptionsBySchool: SubscriptionsBySchool } }
    | { type: 'RESET' }

type BatchState = {
    rows: BatchRow[]
    startDate?: Date
    term: 'year'
    country?: string
    subscriptionsBySchool: SubscriptionsBySchool
}
const initialState: BatchState = {
    rows: [],
    startDate: undefined,
    term: 'year',
    country: undefined,
    subscriptionsBySchool: {}
}

const reducer = (state: BatchState, action: Action): BatchState => {
    switch (action.type) {
        case 'INIT': {
            const { rows } = action.payload

            return {
                ...state,
                rows: [...rows]
            }
        }
        case 'UPDATE_ROW_STATUS': {
            const { index, status, isValid } = action.payload

            if (state.rows.length <= index) {
                throw new Error('Index does not exist')
            }
            const row = { ...state.rows[index] }
            // do stuff on the row
            if (status) {
                row.status = status
            }

            if (typeof isValid === 'boolean') {
                row.isValid = isValid
            }

            const updatedRows = [...state.rows]
            updatedRows.splice(index, 1, row)

            return {
                ...state,
                rows: updatedRows as BatchRow[]
            }
        }
        case 'UPDATE_ROW_ACTION': {
            const { index, rowAction } = action.payload

            if (state.rows.length <= index) {
                throw new Error('Index does not exist')
            }
            const row = { ...state.rows[index] }
            // do stuff on the row
            if (rowAction) {
                row.actionType = rowAction.type
                if (rowAction.type === 'ExistingSchool') {
                    if (rowAction.schoolId) {
                        row.schoolId = rowAction.schoolId
                    } else {
                        throw new Error('Existing school ID does not exist')
                    }
                    if (rowAction.seatsComparison) {
                        row.seatsComparison = rowAction.seatsComparison
                    }
                }
            }

            const updatedRows = [...state.rows]
            updatedRows.splice(index, 1, row)

            return {
                ...state,
                rows: updatedRows as BatchRow[]
            }
        }
        case 'ADD_MESSAGE': {
            const { index, message, type } = action.payload

            if (state.rows.length <= index) {
                throw new Error('Index does not exist')
            }
            const row = { ...state.rows[index] }

            const errors = [...row.errors]
            errors.push({
                message,
                type
            })
            row.errors = errors

            const updatedRows = [...state.rows]
            updatedRows.splice(index, 1, row)

            return {
                ...state,
                rows: updatedRows as BatchRow[]
            }
        }
        case 'UPDATE_START_DATE': {
            const { startDate } = action.payload

            return {
                ...state,
                startDate
            }
        }
        case 'UPDATE_COUNTRY': {
            const { country } = action.payload

            return {
                ...state,
                country
            }
        }
        case 'UPDATE_EXISTING_SUBSCRIPTIONS': {
            const { subscriptionsBySchool } = action.payload
            return {
                ...state,
                subscriptionsBySchool
            }
        }
        case 'RESET': {
            return {
                ...state,
                startDate: new Date(),
                country: undefined,
                rows: []
            }
        }
        default: {
            return state
        }
    }
}

export const useBatch = () => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const confirm = useConfirmation()
    const { showNotification } = useNotifications()
    const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('NOT_STARTED')

    const init = (rows: BatchRow[]) => {
        dispatch({ type: 'INIT', payload: { rows } })
    }

    const updateRowStatus = (index: number, { status, isValid }: { status?: RowStatusTypes; isValid?: boolean }) => {
        dispatch({
            type: 'UPDATE_ROW_STATUS',
            payload: {
                index,
                status,
                isValid
            }
        })
    }

    const updateRowAction = (index: number, rowAction: RowAction) => {
        dispatch({
            type: 'UPDATE_ROW_ACTION',
            payload: {
                index,
                rowAction
            }
        })
    }

    const addMessage = (index: number, { message, type }: { message: string; type: MessageType }) => {
        dispatch({
            type: 'ADD_MESSAGE',
            payload: {
                index,
                message,
                type
            }
        })
    }

    const updateStartDate = (startDate: Date) => {
        dispatch({
            type: 'UPDATE_START_DATE',
            payload: {
                startDate
            }
        })
    }

    const updateCountry = (country: string) => {
        dispatch({
            type: 'UPDATE_COUNTRY',
            payload: {
                country
            }
        })
    }

    const updateSubscriptionsBySchool = (subscriptionsBySchool: SubscriptionsBySchool) => {
        dispatch({
            type: 'UPDATE_EXISTING_SUBSCRIPTIONS',
            payload: {
                subscriptionsBySchool
            }
        })
    }

    const reset = () => {
        setProcessingStatus('NOT_STARTED')
        dispatch({ type: 'RESET' })
    }

    const verifyRows = async (country: string) => {
        if (!state.rows) {
            throw new Error('No subscriptions')
        }

        const subscriptions = await SubscriptionService.listbySchools('ACTIVE')
        const subscriptionsBySchool: SubscriptionsBySchool = normalize(subscriptions, 'schoolSubscription.schoolId')
        updateSubscriptionsBySchool(subscriptionsBySchool)

        const promiseThrottle = new PromiseThrottle({ limit: 2 })

        let readyToProcess = true

        state.rows.forEach(async ({ values }, i) => {
            const { postalCode, domain, govId, products, seats, keyword } = values

            // check if there is a duplicate in the spreadsheet (govId only)
            const duplicateInSheet = state.rows.find((r, j) => {
                return j !== i && r.values.govId === govId
            })

            if (!duplicateInSheet) {
                promiseThrottle.add(async () => {
                    try {
                        let seatsComparison: SeatsComparison | undefined = undefined
                        const found = await SchoolService.fineSearch({
                            postalCode,
                            name: keyword,
                            domain,
                            country,
                            govId,
                            offset: 0,
                            limit: 10
                        })

                        if (found.totalCount > 1) {
                            throw new Error('A school was found with the same name')
                        }

                        if (found.totalCount === 1) {
                            const subscriptions = subscriptionsBySchool[found.schools[0].id]

                            const futureSubscriptions = subscriptions.filter(subscription => {
                                const now = new Date()

                                // product overlap check
                                const overlap = subscription.subscriptionProducts.some(sp =>
                                    products.find(p => p.id === sp.product.id)
                                )

                                return (
                                    subscription.status === 'INACTIVE' &&
                                    now < new Date(subscription.startDate) && // between the start and end date (not fully necessary)
                                    now < new Date(subscription.endDate) &&
                                    overlap
                                )
                            })

                            if (futureSubscriptions.length > 0) {
                                throw new Error('Already has an overlapping future subscription')
                            }

                            const activeSubscriptions = subscriptions.filter(subscription => {
                                const now = new Date()

                                // product overlap check
                                const overlap = subscription.subscriptionProducts.some(sp =>
                                    products.find(p => p.id === sp.product.id)
                                )

                                return (
                                    subscription.status === 'ACTIVE' &&
                                    new Date(subscription.startDate) < now && // between the start and end date (not fully necessary)
                                    now < new Date(subscription.endDate) &&
                                    overlap
                                )
                            })

                            if (activeSubscriptions.length === 1) {
                                // we have a subscription we can copy off of

                                if (activeSubscriptions[0].schoolSubscription) {
                                    seatsComparison = {
                                        old: activeSubscriptions[0].schoolSubscription.seatCount,
                                        new: seats
                                    }
                                }
                            }
                        }

                        const rowAction = (
                            found.totalCount === 1
                                ? { type: 'ExistingSchool', schoolId: found.schools[0].id, seatsComparison }
                                : { type: 'NewSchool' }
                        ) as RowAction

                        updateRowAction(i, rowAction)
                        updateRowStatus(i, { status: 'READY_TO_PROCESS' })
                    } catch (err: any) {
                        if (err.message) {
                            addMessage(i, { message: err.message, type: 'ERROR' })
                        }

                        readyToProcess = false
                        updateRowStatus(i, { status: 'VERIFICATION_FAILED' })
                    }
                })
            } else {
                addMessage(i, { message: 'Duplicate in the sheet', type: 'ERROR' })

                readyToProcess = false
                updateRowStatus(i, { status: 'VERIFICATION_FAILED' })
            }
        })

        await promiseThrottle.start()

        if (readyToProcess) {
            showNotification({ title: 'Ready to process' })
            setProcessingStatus('READY_TO_PROCESS')
        } else {
            showNotification({ title: 'Some rows are not processable', type: 'error' })
            setProcessingStatus('CAN_NOT_PROCESS')
        }
    }

    const processBatch = async () => {
        if (
            await confirm({
                title: 'Do you want to process these schools?'
            })
        ) {
            if (!state.rows) {
                throw new Error('No subscriptions')
            }

            const { startDate } = state

            if (typeof startDate === 'undefined') {
                throw new Error('Start date has not been set')
            }

            const endDate = dayjs(state.startDate).add(1, state.term)

            const { country } = state

            if (!country) {
                throw new Error('Country not selected')
            }

            setProcessingStatus('PROCESSING')

            const promiseThrottle = new PromiseThrottle({ limit: 2 })

            const { subscriptionsBySchool } = state

            state.rows.forEach(async ({ actionType, schoolId: passedSchoolId, values }, i) => {
                const { schoolName, seats, adminEmails, products, postalCode, domain } = values
                promiseThrottle.add(async () => {
                    try {
                        let schoolId
                        let seatsToCopy: string[] = []
                        if (actionType === 'NewSchool') {
                            const body = {
                                name: schoolName,
                                country,
                                postalCode,
                                domain
                            }

                            // Add the school
                            const school = await SchoolService.create(body)
                            schoolId = school.id
                        } else if (actionType === 'ExistingSchool' && passedSchoolId) {
                            schoolId = passedSchoolId

                            const subscriptions = subscriptionsBySchool[schoolId]

                            // check active subscription exists that we can copy off of
                            const activeSubscriptions = subscriptions.filter(subscription => {
                                const now = new Date()

                                // product overlap check
                                const overlap = subscription.subscriptionProducts.some(sp =>
                                    products.find(p => p.id === sp.product.id)
                                )

                                return (
                                    subscription.status === 'ACTIVE' &&
                                    new Date(subscription.startDate) < now && // between the start and end date (not fully necessary)
                                    now < new Date(subscription.endDate) &&
                                    overlap
                                )
                            })

                            if (activeSubscriptions.length === 1) {
                                // we have a subscription we can copy off of

                                const schoolUsersToCopy =
                                    activeSubscriptions[0].schoolSubscription?.schoolSubscriptionSeats!.map(
                                        seats => seats.schoolUserId
                                    )

                                if (schoolUsersToCopy && schoolUsersToCopy.length > 0) {
                                    seatsToCopy = schoolUsersToCopy
                                }
                            }
                        } else {
                            throw new Error('Improper school setup')
                        }

                        const creatorId = products[0].creatorId
                        // Create new subscription
                        const formattedValues = {
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            schoolId: schoolId,
                            productIds: products.map(product => product.id),
                            status: 'INACTIVE' as SubscriptionStatus,
                            seatCount: seats,
                            term: 'YEARLY' as TermType,
                            name: `Maths — No Problem! ${getYearLabel(startDate, endDate.toDate())}`,
                            creatorId
                        }
                        // Add the subscription
                        const subscriptionResult = await SchoolService.addSubscription(schoolId, formattedValues)
                        // Get the subscription (we want the schoolSubscription id)
                        const fetchedSubscription = await SchoolService.getSubscription(schoolId, subscriptionResult.id)
                        // Can not find a school subscription or school user to link together
                        if (!fetchedSubscription.schoolSubscription) {
                            updateRowStatus(i, { status: 'FAILED' })
                            return
                        }

                        const seatReduction = seatsToCopy.filter((s, i) => i < seats)

                        await SchoolService.assignUsers(schoolId, {
                            add: {
                                [fetchedSubscription.schoolSubscription.id]: seatReduction
                            },
                            remove: {}
                        })

                        const school = await SchoolService.get(schoolId)

                        for (let i = 0; i < adminEmails.length; i += 1) {
                            const adminEmail = adminEmails[i]

                            const foundAdmin = school.schoolUsers.find(
                                schoolUser => schoolUser.user.email.toLowerCase() === adminEmail.toLowerCase()
                            )

                            if (foundAdmin && foundAdmin.role !== 'ADMIN') {
                                await SchoolService.updateUser(schoolId, foundAdmin.user.id, {
                                    role: 'ADMIN',
                                    notifyOnSeatAddition: true
                                })
                            }

                            // If admin email is not already in the system - add it in
                            if (!foundAdmin) {
                                // Check if they've been invited before
                                // if yes, delete invite and reinvite
                                const foundInvite = await SchoolService.searchInvite(schoolId, adminEmail)
                                if (foundInvite) {
                                    await SchoolService.removeInvite(schoolId, foundInvite.id)
                                }

                                const inviteUserBody: {
                                    email: string
                                    role: string
                                    schoolSubscriptionIds: string[]
                                } = {
                                    email: adminEmail,
                                    role: 'ADMIN',
                                    schoolSubscriptionIds: []
                                }

                                // check if subscription will fit
                                if (seatReduction.length < seats) {
                                    if (!fetchedSubscription.schoolSubscription) {
                                        // this is already checked above. ts/lint is complaining
                                        throw new Error(
                                            'School subscription not found. Error should have already been caught in prior code'
                                        )
                                    }
                                    inviteUserBody.schoolSubscriptionIds.push(fetchedSubscription.schoolSubscription.id)
                                }

                                // Invite the email to the school
                                // Only queue it for now
                                await SchoolService.invite(schoolId, inviteUserBody, { queueOnly: true })
                            }
                        }

                        // check if adminEmail has been invited
                        // if not, invite new user

                        updateRowStatus(i, { status: 'COMPLETE' })
                    } catch (err: any) {
                        if (err.message) {
                            addMessage(i, { message: err.message, type: 'ERROR' })
                        }
                        updateRowStatus(i, { status: 'FAILED' })
                    }
                })
            })

            await promiseThrottle.start()

            showNotification({ title: 'Processing complete' })

            setProcessingStatus('COMPLETE')
        }
    }

    const setRowToVerified = (i: number) => {
        updateRowStatus(i, { status: 'READY_TO_PROCESS' })
    }

    const canProcess = !!state.startDate && !!state.country

    return {
        init,
        updateRowStatus,
        updateRowAction,
        updateStartDate,
        updateCountry,
        addMessage,
        rows: state.rows,
        reset,
        processBatch,
        verifyRows,
        processingStatus,
        setProcessingStatus,
        canProcess,
        setRowToVerified,
        country: state.country
    }
}
